// Phase 9 — Dashboard-first UI rebalance. Module grid.
//
// Responsive 3-column → 2-column → 1-column grid of ModuleCard tiles.
// One card per enabled module. Placeholder cards for modules that are declared
// in the registry but have no data yet (coming-soon state).

import type { DashboardCard } from '@dailydeck/shared/types';
import { ModuleCard } from './ModuleCard.js';

const COMING_SOON_MODULES: readonly DashboardCard[] = [
  {
    cardId: 'tasks-summary',
    title: 'Tasks',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'tasks',
    action_url: '/tasks',
  },
  {
    cardId: 'habits-summary',
    title: 'Habits',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'habits',
    action_url: '/habits',
  },
  {
    cardId: 'notes-summary',
    title: 'Notes',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'notes',
    action_url: '/notes',
  },
  {
    cardId: 'goals-summary',
    title: 'Goals',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'goals',
    action_url: '/goals',
  },
  {
    cardId: 'reading-summary',
    title: 'Reading',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'reading',
    action_url: '/reading',
  },
  {
    cardId: 'subscriptions-summary',
    title: 'Subscriptions',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'subscriptions',
    action_url: '/subscriptions',
  },
  {
    cardId: 'travel-summary',
    title: 'Travel',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'travel',
    action_url: '/travel',
  },
  {
    cardId: 'inventory-summary',
    title: 'Home',
    subtitle: 'Coming soon',
    priority: 'info',
    module_id: 'inventory',
    action_url: '/inventory',
  },
];

export interface ModuleGridProps {
  readonly cards: readonly DashboardCard[];
}

export function ModuleGrid({ cards }: ModuleGridProps): JSX.Element {
  // Dedupe: one card per module_id. The first card for each module wins.
  const seen = new Set<string>();
  const moduleCards: DashboardCard[] = [];
  for (const card of cards) {
    const moduleId = card.module_id ?? card.cardId;
    if (!seen.has(moduleId)) {
      seen.add(moduleId);
      moduleCards.push(card);
    }
  }

  // Append coming-soon placeholders for modules not yet built.
  // Only show them when we have at least one real card (i.e. user is
  // authenticated and the dashboard loaded successfully).
  if (moduleCards.length > 0) {
    for (const cs of COMING_SOON_MODULES) {
      if (!seen.has(cs.module_id!)) {
        moduleCards.push(cs);
      }
    }
  }

  return (
    <section aria-label="Modules">
      <h2 className="text-title-3 font-semibold text-text-primary mb-4">
        Modules
      </h2>
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="module-grid"
      >
        {moduleCards.map((card) => (
          <ModuleCard
            key={`${card.module_id ?? 'unknown'}:${card.cardId}`}
            card={card}
          />
        ))}
      </div>
    </section>
  );
}
